'use client';

import { useEffect } from 'react';

import { api, useMutation } from '@/lib/convex';
import {
  endpointOwnershipAction,
  hasBrowserNotificationOptOut,
  isWebPushSupported,
  localPushSubscriptionForOwnership,
  serializePushSubscription,
} from '@/lib/notifications/web-push-client';

// Once per document: signing in or out always loads a new one.
let checked = false;

/**
 * On sign-in, makes this browser's push subscription belong to the account
 * that is signed in now, or to no one (endpointOwnershipAction). Tabs take
 * turns through a Web Lock, so a second tab finds the first tab's result
 * (`touch` reports it owned) and keeps it.
 */
export function useWebPushOwnership(enabled: boolean) {
  const touch = useMutation(
    api.notifications.subscriptions.touchWebPushSubscription
  );
  const register = useMutation(
    api.notifications.subscriptions.registerWebPushSubscription
  );
  const release = useMutation(
    api.notifications.subscriptions.releaseWebPushEndpoint
  );

  useEffect(() => {
    if (!enabled || checked || !isWebPushSupported()) return;
    checked = true;
    const reconcile = async () => {
      const subscription = await localPushSubscriptionForOwnership();
      if (!subscription) return;
      const { touched } = await touch({ endpoint: subscription.endpoint });
      const action = endpointOwnershipAction({
        ownedByCurrentUser: touched,
        permission: Notification.permission,
        optedOut: hasBrowserNotificationOptOut(),
      });
      if (action === 'keep') return;
      if (action === 'adopt') {
        await register({
          ...serializePushSubscription(subscription),
          deviceLabel: navigator.userAgent.slice(0, 60),
        });
        return;
      }
      await release({ endpoint: subscription.endpoint });
      await subscription.unsubscribe().catch(() => false);
    };
    const run =
      typeof navigator.locks?.request === 'function'
        ? navigator.locks.request('imai-push-ownership', reconcile)
        : reconcile();
    void Promise.resolve(run).catch(() => {
      // Try again on the next mount (the sender's checks still apply).
      checked = false;
    });
  }, [enabled, touch, register, release]);
}
